import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription, combineLatest, filter } from 'rxjs';
import { Command } from 'src/app/model/command';
import { DeviceStatus } from 'src/app/model/device-status';
import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { selectCurrentDevice, selectCurrentDeviceCommands, selectCurrentDeviceModel, selectCurrentDeviceStatus } from 'src/app/store/device/device.selector';
import { CommandAliasDialogComponent } from '../command-alias-dialog/command-alias-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Device } from 'src/app/model/device';
import { EDeviceModel } from 'src/app/conf/device-models';
import { DeviceModel } from 'src/app/model/device-model';
import { StandardDesignParameters } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import * as AnalyticsActions from 'src/app/store/analytics/analytics.actions';
import { selectIsDraftMode, selectIsImpersMode } from 'src/app/store/user/user.selector';

@Component({
	selector: 'app-commands',
	templateUrl: './commands.component.html',
	providers: [DialogService],
	styles: `
		:host ::ng-deep .p-panel-header {
			border: 1px solid #D5D5D5 !important;
			border-bottom: none !important;
		}
		:host ::ng-deep .p-panel-content {
			border: 1px solid #D5D5D5 !important;
		}
	`
})

export class CommandsComponent implements OnInit, OnDestroy {
	commands$: Observable<Command[] | undefined>;
	commandsSub: Subscription | undefined;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;
	device$: Observable<Device | undefined>;
	deviceSub: Subscription | undefined;
	deviceModel$: Observable<DeviceModel | undefined>;
	deviceModelSub: Subscription | undefined;
	isImpersMode$: Observable<boolean | undefined>;
	isDraftMode$: Observable<boolean | undefined>;
	isTechnicianSub: Subscription | undefined;

	currentDevice: Device;
	deviceModel: DeviceModel = new DeviceModel();

	isTechnician: boolean = false;
	commands: Command[];
	deviceOnline: boolean;
	deviceConfigured: boolean;
	ref: DynamicDialogRef | undefined;

	scanConnectedAutomation: boolean;

	// Design
	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}


	initSelectors(): void {
		this.commands$ = this.store.select(selectCurrentDeviceCommands);
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.device$ = this.store.select(selectCurrentDevice);
		this.deviceModel$ = this.store.select(selectCurrentDeviceModel);
		this.isImpersMode$ = this.store.select(selectIsImpersMode);
		this.isDraftMode$ = this.store.select(selectIsDraftMode);
	}

	ngOnInit(): void {
		this.store.dispatch(DeviceActions.getCommands({}));
		this.commandsSub = this.commands$.subscribe(
			commands => {
				this.commands = this.filterCommands(commands);
			}
		);
		this.deviceStatusSub = this.deviceStatus$.pipe(filter(ds => !!ds))
			.subscribe(deviceState => {
				this.deviceOnline = deviceState.Online;
				this.deviceConfigured = deviceState.Configured === 0;
				this.scanConnectedAutomation = deviceState.ScanConnectedAutomation;
			});
		this.deviceSub = this.device$.subscribe(currentDevice => {
			this.currentDevice = currentDevice;
		});
		this.deviceModelSub = this.deviceModel$.pipe(filter(dm => !!dm))
			.subscribe(deviceModel => {
				this.deviceModel = deviceModel;
			});
		this.isTechnicianSub = combineLatest([this.isImpersMode$, this.isDraftMode$]).subscribe(
			([isImpersMode, isDraftMode]) => {
				this.isTechnician = !!isImpersMode || !!isDraftMode;
			}
		);
		this.onResize();
	}

	ngOnDestroy(): void {
		this.commandsSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.deviceSub?.unsubscribe();
		this.deviceModelSub?.unsubscribe();
		this.isTechnicianSub?.unsubscribe();
	}

	filterCommands(commands: Command[]): Command[] {
		const findComm3 = commands.find(cm => cm.Id === 3);
		const commandListNew = [];
		for (const comm of commands) {
			if (comm.Id !== 2 || (comm.Id === 2 && findComm3 === undefined)) {
				commandListNew.push(comm);
			}
		}
		return commandListNew;
	}

	deviceConfiguredOnline(): boolean {
		return this.deviceConfigured && this.deviceOnline;
	}

	executeCommand(commandId: number): void {
		this.store.dispatch(DeviceActions.executeCommand({ commandId }));
		setTimeout(() => {
			this.store.dispatch(AnalyticsActions.eventDevice({ eventName: 'execute_command', params: { page_title: 'Device', commandId: commandId } }));
		}, 2000);
	}

	editAlias(): void {
		this.store.dispatch(DeviceActions.resetEditCommands());
		this.ref = this.dialogService.open(CommandAliasDialogComponent, {
			header: this.translate.instant('COMMANDS_ALIAS'),
			width: '70vw',
			breakpoints: { '1250px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	order(comm: Command[]): Command[] {
		const newCommand = [];
		const endCommand = [];
		for (const c of comm) {
			if ((c.Id >= 131 && c.Id <= 136) || c.Id === 1) {
				endCommand.push(c);
			}
			else {
				newCommand.push(c);
			}
		}
		return newCommand.concat(endCommand);
	}

	getColor(comm: Command): string {
		if ((comm.Id >= 130 && comm.Id <= 136) || comm.Id === 1) {
			return 'warning';
		}
		return 'primary';
	}

	manualScan(): void {
		this.store.dispatch(DeviceActions.getAutomationDiscover({ deviceId: this.currentDevice.Id }));
	}


	isRbe24230(): boolean {
		return this.deviceModel !== undefined && (this.deviceModel.Id === EDeviceModel.RBE24230 || this.deviceModel.Id === EDeviceModel.RBE230 ||
			this.deviceModel.Id === EDeviceModel.RBE24 || this.deviceModel.Id === EDeviceModel.GEN_RBE24230);
	}

	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	underTablet(): boolean {
		return (this.screenWidth <= StandardDesignParameters.MIN_MD_WIDTH);
	}

}
